import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Gamepad2, Play, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
    mapApiProviderToComponent,
    excludeMaintenanceProviders,
    hasIconForProvider,
    getIconPathForProvider,
    type GameProvider,
    type GameProviderApiItem,
} from '@/features/games/data/gameProviders';
import { GameBalanceAmount } from '@/features/games/utils/gameBalanceView';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useScrollingClass } from '@/hooks/useScrollingClass';
import { useHorizontalDragScroll } from '@/hooks/useHorizontalDragScroll';
import { playClickSound } from '@/utils/soundUtils';
import { notificationService } from '@/services/notificationService';
import { getGameProviders, getCachedProvidersSync, preloadProviderIcons } from '@/features/games/services/gameProviderService';
import { isProviderDepositLocked, mergeProvidersWithHealth } from '@/features/games/services/gameProviderHealthService';
import {
    appStoreSpacing,
    computeHomeGridLayout,
    fallbackHomeGridLayout,
    getHomeGridBreakpointConfig,
    homeGridLayoutEqual,
    type HomeGridLayout,
} from '@/features/games/utils/homeGridLayout';
import { getUserGameWallets, type GameWalletItem } from '@/features/finance/services/paymentService';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { useUiScale } from '@/contexts/DeviceModeContext';
import type { DeviceClass, Orientation } from '@/config/uiScale';
import APP_CONFIG from '@/config/appConfig';
import OrderSummaryFooter from '@/layouts/components/Footer/OrderSummaryFooter';
import OrderSummaryModal from '@/features/finance/components/OrderSummaryModal';
import ManualDepositWarningModal from '@/features/finance/components/ManualDepositWarningModal';
import GameSheet from '@/features/games/components/GameSheet';
import PaymentMethodsStrip from '@/features/games/components/PaymentMethodsStrip';
import GuestHero from '@/features/home/guestHero/GuestHero';
import { walletForGame } from '@/features/games/utils/sheetGame';
import { usePlayGame } from '@/features/games/hooks/usePlayGame';
import {
    Alert,
    Button,
    Card,
    EmptyState,
    FilterChip,
    FilterChips,
    GameTile,
    IconButton,
    PageShell,
    SearchField,
    Skeleton,
    cx,
} from '@/components/ui';

import {
    buildHomeProviderTagFilters,
    CONTINUE_FALLBACK_COUNT,
    HOME_TAG_VISIBLE_MAX,
    isHomeProviderTagFilterActive,
    matchesHomeProviderTag,
    pickContinueFallbackProviders,
    sortUnavailableGamesLast,
} from '@/features/games/utils/homeProviderFilters';

// DEMO MODE - REMOVE IN PRODUCTION
import DemoWrapper from '@/components/demo/DemoWrapper';
import {
    ANIMATION_REGISTRY,
    getSharedShineClockStyle,
    getWaveTileStyle,
} from '@/config/animationRegistry';
import { useRouteAnimation } from '@/contexts/AnimationContext';

const MAX_DEPOSIT_AMOUNT = 10000;
const HOME_PROVIDER_COUNT_KEY = 'kiosk:home:providerCount';
const HOME_SKELETON_MIN_ROWS = 4;

function readHomeSkeletonTileCount(cols: number): number {
    if (typeof window === 'undefined') {
        return cols * HOME_SKELETON_MIN_ROWS;
    }
    const stored = Number.parseInt(sessionStorage.getItem(HOME_PROVIDER_COUNT_KEY) || '0', 10);
    const estimated = stored > 0 ? stored : cols * HOME_SKELETON_MIN_ROWS;
    return Math.max(cols * 2, Math.ceil(estimated / cols) * cols);
}

function readInitialProvidersFromCache(): { providers: GameProvider[]; loading: boolean } {
    if (typeof window === 'undefined') {
        return { providers: [], loading: true };
    }
    const cached = getCachedProvidersSync();
    if (cached?.success && cached.data?.items?.length) {
        const mapped = excludeMaintenanceProviders(
            cached.data.items
                .map(mapApiProviderToComponent)
                .filter((provider): provider is GameProvider => provider !== null)
        );
        if (mapped.length > 0) {
            return { providers: mapped, loading: false };
        }
    }
    return { providers: [], loading: true };
}

function homeViewportWidth(): number {
    return typeof document !== 'undefined'
        ? document.documentElement.clientWidth
        : typeof window !== 'undefined'
          ? window.innerWidth
          : 375;
}


/**
 * Home tidal wave plays once per Home mount (when content first becomes ready).
 * Stays "armed" for the rest of the mount so wave class/coords are not stripped
 * (stripping re-rendered the whole Home and looked like a scale jump).
 * After the sweep, marks `data-home-wave-done` on the shell via DOM — CSS kills
 * the animation without a React state update. Filter remounts inherit done → no replay.
 */
function useHomeWaveOnMount(
    enabled: boolean,
    contentReady: boolean,
    maxDiagonal: number,
    shellEl: HTMLElement | null
): boolean {
    const [armed, setArmed] = useState(false);
    const startedThisMountRef = useRef(false);
    const finishedRef = useRef(false);

    useEffect(() => {
        if (!enabled) {
            setArmed(false);
            startedThisMountRef.current = false;
            finishedRef.current = false;
            shellEl?.removeAttribute('data-home-wave-done');
            return;
        }
        if (!contentReady || startedThisMountRef.current) return;
        startedThisMountRef.current = true;
        finishedRef.current = false;
        setArmed(true);
    }, [enabled, contentReady, shellEl]);

    useEffect(() => {
        if (!armed || !shellEl || finishedRef.current) return undefined;
        shellEl.removeAttribute('data-home-wave-done');
        const sweepMs =
            ANIMATION_REGISTRY.wave.durationMs +
            ANIMATION_REGISTRY.wave.staggerMs * Math.max(0, maxDiagonal) +
            80;
        const t = window.setTimeout(() => {
            finishedRef.current = true;
            shellEl.setAttribute('data-home-wave-done', '');
        }, sweepMs);
        return () => window.clearTimeout(t);
    }, [armed, maxDiagonal, shellEl]);

    return armed && enabled;
}

/** Grid layout value (design px) → rendered CSS px at the current UI scale. */
function homeGridCssPx(designPx: number, scale: number): string {
    return `${Math.round(designPx * scale * 100) / 100}px`;
}

/**
 * Home browse grid: cols + slot from the width of the browse column (inside the PageShell
 * gutters, stable while scrolling). The tile gap follows the App Store spacing for that width.
 * Measures CSS px, lays out in design px (÷ scale) for the device class; the caller renders
 * slot and gap × scale.
 */
function useHomeGridLayout(
    contentEl: HTMLElement | null,
    scale: number,
    device: DeviceClass,
    orientation: Orientation
) {
    const [layout, setLayout] = useState<HomeGridLayout>(() =>
        fallbackHomeGridLayout(homeViewportWidth() / scale, device, orientation)
    );

    useLayoutEffect(() => {
        if (!contentEl) return;

        let raf = 0;
        let lastBorderBox = 0;

        const applyFromBorderBox = (borderBox: number) => {
            if (borderBox <= 0) return;
            // Overlay scrollbar / sub-pixel / mobile chrome flicker while scrolling —
            // ignore tiny width noise so tiles don't reflow mid-scroll.
            if (lastBorderBox > 0 && Math.abs(borderBox - lastBorderBox) < 8) {
                return;
            }
            lastBorderBox = borderBox;

            const contentWidth = borderBox / scale;
            const next = computeHomeGridLayout(
                contentWidth,
                getHomeGridBreakpointConfig(contentWidth, device, orientation),
                appStoreSpacing(contentWidth)
            );
            setLayout((prev) => (homeGridLayoutEqual(prev, next) ? prev : next));
        };

        const update = () => {
            applyFromBorderBox(Math.round(contentEl.getBoundingClientRect().width));
        };

        const schedule = () => {
            const borderBox = Math.round(contentEl.getBoundingClientRect().width);
            const delta = lastBorderBox > 0 ? Math.abs(borderBox - lastBorderBox) : Number.POSITIVE_INFINITY;
            // Desktop ↔ mobile jumps: apply before paint to avoid 1-frame tiny-tile flash.
            if (delta >= 40) {
                if (raf) {
                    window.cancelAnimationFrame(raf);
                    raf = 0;
                }
                applyFromBorderBox(borderBox);
                return;
            }
            if (raf) return;
            raf = window.requestAnimationFrame(() => {
                raf = 0;
                update();
            });
        };

        update();
        const ro = new ResizeObserver(schedule);
        ro.observe(contentEl, { box: 'border-box' });

        // Mobile URL-bar show/hide fires resize; only react when width actually moves.
        let lastWinW = window.innerWidth;
        const onWinResize = () => {
            const w = window.innerWidth;
            const winDelta = Math.abs(w - lastWinW);
            if (winDelta < 8) return;
            lastWinW = w;
            if (winDelta >= 40) {
                if (raf) {
                    window.cancelAnimationFrame(raf);
                    raf = 0;
                }
                // Sync now (DevTools device mode / window drag), then one rAF to catch shell lag.
                update();
                raf = window.requestAnimationFrame(() => {
                    raf = 0;
                    update();
                });
                return;
            }
            schedule();
        };
        window.addEventListener('resize', onWinResize);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onWinResize);
            if (raf) window.cancelAnimationFrame(raf);
        };
    }, [contentEl, scale, device, orientation]);

    return layout;
}

/** Shimmer placeholder matching Home `GameTile` (icon + label). */
function HomeGridTileSkeleton() {
    return (
        <div
            className="relative flex min-w-0 w-[var(--gi)] max-w-[var(--gi)] shrink-0 flex-col items-center gap-1"
            aria-hidden
        >
            <Skeleton shape="block" className="size-[var(--gi)] shrink-0" />
            <Skeleton shape="line" className="w-[65%] max-w-[var(--gi)]" />
        </div>
    );
}

/** "Your games" card shell: art · name and balance on top, the money actions underneath. */
const continueCardClass =
    'flex w-60 shrink-0 flex-col gap-3 rounded-container border k-card bg-card p-3 @bp-sm:w-64 @bp-lg:w-72';

/** Reserve the same carousel footprint as loaded "Your games" cards. */
const CONTINUE_SKELETON_CARD_COUNT = 3;

function ContinueCarouselSkeleton({ label }: { label: string }) {
    return (
        <div
            className="flex min-w-0 gap-3 overflow-hidden py-1"
            aria-busy="true"
            aria-label={label}
        >
            {Array.from({ length: CONTINUE_SKELETON_CARD_COUNT }, (_, i) => (
                <div key={i} className={cx(continueCardClass, 'pointer-events-none')} aria-hidden>
                    <div className="flex min-w-0 items-center gap-3">
                        <Skeleton shape="block" className="size-14 shrink-0" />
                        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <Skeleton shape="line" className="w-4/5" />
                            <Skeleton className="h-3.5 w-3/5" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <Skeleton className="h-9" />
                        <Skeleton className="h-9" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * "Your games" card art: Skeleton overlay until the image finishes loading, then fade in.
 * Keeps the game-icon frame (`lobby-gi-ico`) so the Home wave animates it like the grid tiles.
 */
function ContinueCardIcon({ src, alt }: { src: string; alt: string }) {
    const [loaded, setLoaded] = useState(false);
    const [imgSrc, setImgSrc] = useState(src);
    useEffect(() => {
        setLoaded(false);
        setImgSrc(src);
    }, [src]);
    return (
        <div className="lobby-gi-ico relative size-14 shrink-0 overflow-hidden">
            {!loaded && <Skeleton className="absolute inset-0 h-full w-full rounded-none" />}
            <img
                // ponytail: ref catches already-cached images — onLoad does not re-fire so img can stick at opacity-0
                ref={(el) => { if (el?.complete && el.naturalWidth > 0) setLoaded(true); }}
                src={imgSrc}
                alt={alt}
                className={cx('block h-full w-full object-cover pointer-events-none select-none', !loaded && 'opacity-0')}
                draggable={false}
                onLoad={() => setLoaded(true)}
                onError={() => {
                    if (imgSrc !== '/icons/egame.jpg') setImgSrc('/icons/egame.jpg');
                    else setLoaded(true);
                }}
            />
        </div>
    );
}

/**
 * Scrollport of the Home games strip.
 *
 * The arrows are hidden on narrow screens and the scrollbar is hidden everywhere, so without drag
 * scrolling a mouse had no way to reach the cards on the right. A drag never counts as a tap on a
 * card. The drag state lives here so a drag re-renders this one div, not the whole Home page.
 */
const ContinueCarouselViewport = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(
    function ContinueCarouselViewport({ children }, ref) {
        const { scrollRef, dragScrollProps, isDragging } = useHorizontalDragScroll<HTMLDivElement>();
        const setRef = useCallback(
            (node: HTMLDivElement | null) => {
                scrollRef(node);
                if (typeof ref === 'function') ref(node);
                else if (ref) ref.current = node;
            },
            [ref, scrollRef]
        );
        return (
            <div
                ref={setRef}
                {...dragScrollProps}
                className={cx(
                    'min-w-0 flex-1 overflow-x-auto overflow-y-hidden overscroll-x-contain py-1 cosmos-scroll-hidden',
                    isDragging ? 'scroll-auto cursor-grabbing select-none' : 'scroll-smooth'
                )}
            >
                {children}
            </div>
        );
    }
);

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
function KioskGameProvidersContent() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { isAuthenticated, requireAuth } = useAuthModal();
    /** Off kiosks: Play on "Your games" cards ("Your login" dialog or the in-app player). */
    const { canPlay, play, playDialog } = usePlayGame();
    const waveGridEnabled = useRouteAnimation('home.waveGrid');

    const [providers, setProviders] = useState<GameProvider[]>(() => readInitialProvidersFromCache().providers);
    const [isLoadingProviders, setIsLoadingProviders] = useState<boolean>(() => readInitialProvidersFromCache().loading);
    const [providersError, setProvidersError] = useState<string | null>(null);
    // Map to store amounts for each provider (id -> amount)
    const [providerAmounts, setProviderAmounts] = useState<Map<string, number>>(new Map());
    // Search + category filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [tagsExpanded, setTagsExpanded] = useState(false);
    // Order summary popup state
    const [showOrderSummary, setShowOrderSummary] = useState(false);
    // Manual deposit warning state
    const [showManualDepositWarning, setShowManualDepositWarning] = useState(false);
    // Game sheet (tap a tile or a "Your games" card): login, Add money, Take money out
    const [detailProvider, setDetailProvider] = useState<GameProvider | null>(null);
    // Authenticated: linked game wallets ("Continue playing")
    const [gameWallets, setGameWallets] = useState<GameWalletItem[]>([]);
    /** True while the continue-playing wallets are being fetched (reserves layout space) */
    const [walletsLoading, setWalletsLoading] = useState<boolean>(isAuthenticated);

    /** Wave hook only: CSS ends the Home wave on `.lobby-home-bd[data-home-wave-done]`. */
    const [homeShellEl, setHomeShellEl] = useState<HTMLDivElement | null>(null);
    /** Browse column inside the PageShell gutters — its width drives the grid layout. */
    const [homeContentEl, setHomeContentEl] = useState<HTMLElement | null>(null);
    const homeBrowseGridRef = useRef<HTMLDivElement | null>(null);
    const homeBrowseChipsRef = useRef<HTMLDivElement | null>(null);
    const homeScrollRef = useRef<HTMLDivElement | null>(null);
    /** Chip viewport Y before filter change — corrected in useLayoutEffect after paint. */
    const pendingChipAnchorTopRef = useRef<number | null>(null);
    const measuredGridColsRef = useRef<number | null>(null);
    const [homeGridMinHeight, setHomeGridMinHeight] = useState(0);
    const { scale: uiScale, device: uiDevice, orientation: uiOrientation } = useUiScale();
    const homeGridLayout = useHomeGridLayout(homeContentEl, uiScale, uiDevice, uiOrientation);
    const lobbyCols = homeGridLayout.cols;
    const homeGridStyle = useMemo(() => {
        const slot = homeGridCssPx(homeGridLayout.slotGi, uiScale);
        const gap = homeGridCssPx(homeGridLayout.gap, uiScale);
        return {
            '--home-cols': String(homeGridLayout.cols),
            '--home-gi-slot': slot,
            '--home-gap': gap,
            '--gi': slot,
            gridTemplateColumns: `repeat(${homeGridLayout.cols}, minmax(0, 1fr))`,
            gap,
        } as React.CSSProperties;
    }, [homeGridLayout, uiScale]);

    const preloadGameIcons = useCallback((providerItems: Array<{ code?: string }> = []) => {
        void preloadProviderIcons(providerItems.map((item) => item.code)).catch(() => {
            // Silent by design: icon preload must not affect UX flow.
        });
    }, []);

    useEffect(() => {
        if (providers.length > 0 && typeof window !== 'undefined') {
            sessionStorage.setItem(HOME_PROVIDER_COUNT_KEY, String(providers.length));
        }
    }, [providers.length]);

    // System-level scroll-to-top when the detail modal target changes page context
    useScrollToTop('providers', { behavior: 'smooth', delay: 100 });

    // Background comes from PublicLayout / AuthenticatedLayout (bg-cosmos + overlay)

    const refreshGameWallets = useCallback(() => {
        return getUserGameWallets()
            .then((response) => {
                if (response.success && response.data?.items) {
                    setGameWallets(response.data.items);
                }
            })
            .catch((error: unknown) => {
                console.error('[Home] Error loading game wallets:', error);
            });
    }, []);

    // Authenticated: load game wallets for the "Continue playing" strip
    useEffect(() => {
        if (!isAuthenticated) {
            setGameWallets([]);
            setWalletsLoading(false);
            return;
        }
        let cancelled = false;
        setWalletsLoading(true);
        refreshGameWallets().finally(() => {
            if (!cancelled) setWalletsLoading(false);
        });

        // Backend syncs provider balances shortly after /game-wallets/games — refresh once
        const syncRefreshTimer = window.setTimeout(() => {
            if (!cancelled) void refreshGameWallets();
        }, 5000);

        return () => {
            cancelled = true;
            window.clearTimeout(syncRefreshTimer);
        };
    }, [isAuthenticated, refreshGameWallets]);

    // Check if we should show login form (e.g., from "Back to Login" from forgot password)
    useEffect(() => {
        const showLogin = searchParams.get('showLogin');
        if (showLogin === 'true') {
            navigate('/login');
            // Remove the query parameter from URL
            setSearchParams({});
        }
    }, [searchParams, setSearchParams, navigate]);

    // Fetch game providers from API
    useEffect(() => {
        const applyItems = (items: GameProviderApiItem[]): GameProvider[] =>
            excludeMaintenanceProviders(
                items
                    .map(mapApiProviderToComponent)
                    .filter((provider): provider is GameProvider => provider !== null)
            );

        // Paint the catalog at once, then overlay the public health snapshot (fail-open: a failed
        // health call leaves every provider open). `seq` drops a merge superseded by a newer list.
        let seq = 0;
        const showProviders = (list: GameProvider[]) => {
            const current = ++seq;
            setProviders(list);
            void mergeProvidersWithHealth(list)
                .then((merged) => {
                    if (current === seq) setProviders(merged);
                })
                .catch(() => { /* fail-open */ });
        };

        const fetchProviders = async () => {
            // Check API cache first for instant display
            const cached = getCachedProvidersSync();

            if (cached && cached.success && cached.data?.items && cached.data.items.length > 0) {
                const mappedProviders = applyItems(cached.data.items);

                if (mappedProviders.length > 0) {
                    showProviders(mappedProviders);
                    setIsLoadingProviders(false);
                    preloadGameIcons(cached.data.items);
                    // Refresh in background (respect service cache TTL to avoid duplicate bursts)
                    getGameProviders(false).then(freshResponse => {
                        if (freshResponse.success && freshResponse.data?.items && freshResponse.data.items.length > 0) {
                            const freshProviders = applyItems(freshResponse.data.items);
                            if (freshProviders.length > 0) {
                                showProviders(freshProviders);
                                preloadGameIcons(freshResponse.data.items);
                            }
                        }
                    }).catch(() => { /* silent */ });
                    return;
                }
            }

            // No cache - fetch from API with loading state
            setIsLoadingProviders(true);
            setProvidersError(null);

            try {
                const response = await getGameProviders(false);

                if (response.success && response.data?.items && response.data.items.length > 0) {
                    showProviders(applyItems(response.data.items));
                    preloadGameIcons(response.data.items);
                } else {
                    setProvidersError('No game providers available. Please try again later.');
                }
            } catch (error: any) {
                console.error('[Home] Error fetching game providers:', error);
                setProvidersError('Failed to load game providers. Please try again later.');
            } finally {
                setIsLoadingProviders(false);
            }
        };

        fetchProviders();
    }, [preloadGameIcons]);

    // Handle amount changes for providers – memoized to prevent child re-renders
    const handleAmountChange = useCallback((providerId: string, amount: number) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.set(providerId, Math.max(0, Math.min(amount, MAX_DEPOSIT_AMOUNT)));
            return newMap;
        });
    }, []);

    const handleIncrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            const jumpAmount = APP_CONFIG.jumpingEachClick;
            const newAmount = Math.min(currentAmount + jumpAmount, MAX_DEPOSIT_AMOUNT);
            newMap.set(providerId, newAmount);
            return newMap;
        });
    }, []);

    const handleDecrement = useCallback((providerId: string) => {
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            const currentAmount = newMap.get(providerId) || 0;
            if (currentAmount > 0) {
                const jumpAmount = APP_CONFIG.jumpingEachClick;
                const newAmount = Math.max(currentAmount - jumpAmount, 0);
                newMap.set(providerId, newAmount);
            } else {
                newMap.set(providerId, 0);
            }
            return newMap;
        });
    }, []);

    const handleResetAll = useCallback(() => {
        playClickSound();
        setProviderAmounts(new Map());
        setShowOrderSummary(false);
    }, []);

    const handleRemoveItem = useCallback((providerId: string) => {
        playClickSound();
        setProviderAmounts(prev => {
            const newMap = new Map(prev);
            newMap.delete(providerId);
            return newMap;
        });
    }, []);

    /**
     * Gets list of manual game providers that have amounts > 0.
     */
    const getSelectedManualProviders = () => {
        return providers.filter(p => {
            const amount = providerAmounts.get(p.id) || 0;
            return amount > 0 && p.depositType === 'manual';
        });
    };

    /**
     * Gets total amount for selected manual providers.
     */
    const getManualProvidersTotalAmount = () => {
        return getSelectedManualProviders().reduce((sum, p) => sum + (providerAmounts.get(p.id) || 0), 0);
    };

    /**
     * Saves pending game amounts, then gates auth and opens Deposit (wallet-only).
     * Crypto/gateway checkout is no longer used for game funding.
     */
    const executeProceedToFunding = () => {
        // Save provider amounts to sessionStorage
        const amountsArray = Array.from(providerAmounts.entries())
            .filter(([_, amount]) => amount > 0)
            .map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'continue_button'
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        // Close popups
        setShowOrderSummary(false);
        setShowManualDepositWarning(false);
        setDetailProvider(null);

        requireAuth(() => {
            navigate('/account/finance/deposit');
        });
    };

    // Handle proceed to funding
    const handleProceedToFunding = () => {
        playClickSound();

        // Check if any selected provider is manual
        const manualProviders = getSelectedManualProviders();
        if (manualProviders.length > 0) {
            // Show manual deposit warning before proceeding
            setShowManualDepositWarning(true);
            return;
        }

        // No manual games → proceed immediately
        executeProceedToFunding();
    };

    // Memoized calculations – avoid recalculating on every render
    const totalAmount = useMemo(
        () => Array.from(providerAmounts.values()).reduce((sum, amount) => sum + amount, 0),
        [providerAmounts]
    );

    const tagFilters = useMemo(() => buildHomeProviderTagFilters(providers), [providers]);

    const hasTagOverflow = tagFilters.length > HOME_TAG_VISIBLE_MAX;
    const visibleTagFilters =
        tagsExpanded || !hasTagOverflow ? tagFilters : tagFilters.slice(0, HOME_TAG_VISIBLE_MAX);
    const hiddenTagCount =
        hasTagOverflow && !tagsExpanded ? tagFilters.length - HOME_TAG_VISIBLE_MAX : 0;

    useEffect(() => {
        if (!isHomeProviderTagFilterActive(categoryFilter, tagFilters)) {
            setCategoryFilter('all');
        }
    }, [categoryFilter, tagFilters]);

    const filteredCards = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        // Playable games first; under maintenance and Coming soon go last, Hot tag or not.
        return sortUnavailableGamesLast(providers).filter((card) => {
            if (q && !(
                card.name.toLowerCase().includes(q) ||
                (card.tagline && card.tagline.toLowerCase().includes(q)) ||
                (card.code && card.code.toLowerCase().includes(q)) ||
                (card.clientTag && card.clientTag.toLowerCase().includes(q))
            )) {
                return false;
            }
            if (categoryFilter === 'all') return true;
            return matchesHomeProviderTag(card, categoryFilter);
        });
    }, [providers, searchQuery, categoryFilter]);

    // Lock browse-grid height to the full "All" layout so HOT/tag filters don't collapse the page.
    useLayoutEffect(() => {
        if (isLoadingProviders || searchQuery.trim()) return;
        if (categoryFilter !== 'all') return;
        const el = homeBrowseGridRef.current;
        if (!el) return;

        const applyHeight = () => {
            const h = Math.round(el.getBoundingClientRect().height);
            if (h <= 0) return;
            setHomeGridMinHeight((prev) => {
                if (measuredGridColsRef.current !== lobbyCols) {
                    measuredGridColsRef.current = lobbyCols;
                    return h;
                }
                return h > prev ? h : prev;
            });
        };

        applyHeight();
        // Images / fonts can grow the grid after first paint.
        const ro = new ResizeObserver(applyHeight);
        ro.observe(el);
        return () => ro.disconnect();
    }, [categoryFilter, filteredCards, homeGridLayout, isLoadingProviders, lobbyCols, searchQuery]);

    // After filter change: keep chips fixed in the viewport (scroll height may shrink).
    useLayoutEffect(() => {
        const before = pendingChipAnchorTopRef.current;
        if (before == null) return;
        pendingChipAnchorTopRef.current = null;
        const scrollEl = homeScrollRef.current;
        const chipsEl = homeBrowseChipsRef.current;
        if (!scrollEl || !chipsEl) return;
        const after = chipsEl.getBoundingClientRect().top;
        const delta = after - before;
        if (Math.abs(delta) >= 1) {
            scrollEl.scrollTop += delta;
        }
    }, [categoryFilter, filteredCards.length]);

    /*
     * Desktop: fast vertical scroll keeps :hover on tiles under the cursor.
     * Hover uses transform (+ Continue z-index) so those layers paint over neighbors.
     * Flag the scrollport while scrolling so CSS can drop the lift.
     */
    useScrollingClass(homeScrollRef, 360);

    // All linked My-games wallets (for play / lookup)
    const linkedWallets = useMemo(
        () => gameWallets.filter((w) => !!w.account?.gameUsername && w.game),
        [gameWallets]
    );

    /** Continue playing: linked games sorted by balance — manual carousel scroll. */
    const continuePlayingWallets = useMemo(
        () =>
            [...linkedWallets].sort(
                (a, b) =>
                    (b.balance?.inGameBalance ?? 0) - (a.balance?.inGameBalance ?? 0)
            ),
        [linkedWallets]
    );

    /**
     * When the user has no linked accounts, keep the Continue strip filled with
     * tagged/popular games so skeleton → empty does not collapse the layout.
     */
    const continueFallbackProviders = useMemo(() => {
        if (continuePlayingWallets.length > 0) return [];
        return pickContinueFallbackProviders(providers, CONTINUE_FALLBACK_COUNT);
    }, [continuePlayingWallets.length, providers]);

    /** Replay tidal wave each time Home mounts (when content is ready) */
    const homeWaveContentReady =
        continuePlayingWallets.length > 0 ||
        continueFallbackProviders.length > 0 ||
        (!isLoadingProviders && filteredCards.length > 0);
    const homeSkeletonTileCount = useMemo(
        () => readHomeSkeletonTileCount(lobbyCols),
        [lobbyCols]
    );

    /**
     * The Continue playing strip is the visual "row 0" above the grid when the
     * user is signed in — offset grid wave rows by 1 so the diagonal wave
     * flows continuously from the strip down through the grid.
     */
    const continueStripLoading =
        isAuthenticated &&
        (walletsLoading ||
            (!walletsLoading &&
                continuePlayingWallets.length === 0 &&
                isLoadingProviders &&
                providers.length === 0));
    const showContinueWallets =
        isAuthenticated && !walletsLoading && continuePlayingWallets.length > 0;
    const showContinueFallback =
        isAuthenticated &&
        !walletsLoading &&
        continuePlayingWallets.length === 0 &&
        continueFallbackProviders.length > 0;
    const showContinuePlaying = showContinueWallets || showContinueFallback;
    const showContinueBlock = continueStripLoading || showContinuePlaying;
    const waveRowOffset = showContinuePlaying ? 1 : 0;

    const waveMaxDiagonal = useMemo(() => {
        const cols = Math.max(1, lobbyCols);
        const gridRows = Math.max(1, Math.ceil(filteredCards.length / cols));
        const gridDiag = waveRowOffset + (gridRows - 1) + (cols - 1);
        const continueCount = showContinueWallets
            ? continuePlayingWallets.length
            : showContinueFallback
              ? continueFallbackProviders.length
              : 0;
        const continueDiag = showContinuePlaying ? Math.max(0, continueCount - 1) : 0;
        return Math.max(gridDiag, continueDiag);
    }, [
        continueFallbackProviders.length,
        continuePlayingWallets.length,
        filteredCards.length,
        lobbyCols,
        showContinueFallback,
        showContinuePlaying,
        showContinueWallets,
        waveRowOffset,
    ]);

    /**
     * Shared shine clock for the browse grid. Recalculated when the visible set
     * changes so remounted tiles stay phase-locked to each other (and to header `.btn-shine`).
     */
    const homeBrowseGridStyle = useMemo(() => {
        if (!waveGridEnabled) return homeGridStyle;
        return {
            ...homeGridStyle,
            ...getSharedShineClockStyle({
                '--shine-stagger': `${ANIMATION_REGISTRY.gridShine.staggerMs}ms`,
            }),
        } as React.CSSProperties;
        // Intentionally keyed to filter/layout — not filteredCards identity each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeGridStyle, categoryFilter, searchQuery, lobbyCols, filteredCards.length, waveGridEnabled]);

    // Wave must still play when providers hydrate from cache (common after first visit).
    // ff22a5d gated this with !bootedFromProviderCache and effectively killed home wave.
    const playHomeWave = useHomeWaveOnMount(
        waveGridEnabled,
        homeWaveContentReady,
        waveMaxDiagonal,
        homeShellEl
    );

    const continueCarouselRef = useRef<HTMLDivElement | null>(null);
    const [canScrollContinuePrev, setCanScrollContinuePrev] = useState(false);
    const [canScrollContinueNext, setCanScrollContinueNext] = useState(false);
    const [continueCarouselOverflow, setContinueCarouselOverflow] = useState(() => isAuthenticated);

    const updateContinueCarouselNav = useCallback(() => {
        const el = continueCarouselRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        setContinueCarouselOverflow(scrollWidth > clientWidth + 2);
        setCanScrollContinuePrev(scrollLeft > 4);
        setCanScrollContinueNext(scrollLeft + clientWidth < scrollWidth - 4);
    }, []);

    useLayoutEffect(() => {
        const el = continueCarouselRef.current;
        if (!el) return;
        updateContinueCarouselNav();
        const ro = new ResizeObserver(updateContinueCarouselNav);
        ro.observe(el);
        el.addEventListener('scroll', updateContinueCarouselNav, { passive: true });
        return () => {
            ro.disconnect();
            el.removeEventListener('scroll', updateContinueCarouselNav);
        };
    }, [
        continueStripLoading,
        continuePlayingWallets.length,
        continueFallbackProviders.length,
        updateContinueCarouselNav,
    ]);

    const scrollContinueCarousel = (direction: -1 | 1) => {
        const el = continueCarouselRef.current;
        if (!el) return;
        playClickSound();
        const card = el.querySelector<HTMLElement>('[data-continue-card]');
        const cardWidth = card?.offsetWidth ?? 0;
        const gap = Number.parseFloat(getComputedStyle(el.firstElementChild ?? el).columnGap) || 0;
        const step = cardWidth > 0 ? cardWidth + gap : Math.round(el.clientWidth * 0.9);
        el.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    const normalizeCode = (code: string | undefined | null) =>
        (code || '').toLowerCase().replace(/\s+/g, '').replace(/-/g, '');

    const notifyWalletComingSoon = (wallet: GameWalletItem) => {
        notificationService.info(
            t('home.comingSoonToast', '{{name}} is coming soon — not available yet.', {
                name: wallet.game?.name || t('home.comingSoon', 'Coming soon'),
            })
        );
    };

    const openDetail = (provider: GameProvider) => {
        playClickSound();
        if (provider.status === 'inactive') {
            notificationService.info(
                t('home.comingSoonToast', '{{name}} is coming soon — not available yet.', {
                    name: provider.name,
                })
            );
            return;
        }
        setDetailProvider(provider);
    };

    const handleSearchChange = (value: string) => {
        setSearchQuery(value.slice(0, 40));
    };

    /** FilterChips plays the click sound. */
    const setFilter = (filter: string) => {
        if (filter === categoryFilter) return;
        const chipsEl = homeBrowseChipsRef.current;
        if (chipsEl) {
            pendingChipAnchorTopRef.current = chipsEl.getBoundingClientRect().top;
        }
        setCategoryFilter(filter);
    };

    const comingSoonLabel = t('home.comingSoon', 'Coming soon');
    const detailWallet = detailProvider ? walletForGame(detailProvider, gameWallets) : null;

    return (<div className="relative flex h-full max-h-full min-w-0 flex-col overflow-hidden">
        {/* Body scrolls */}
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
            <div
                ref={homeScrollRef}
                className="lobby-home-scroll min-h-0 flex-1 cosmos-scroll-y overflow-x-hidden overscroll-contain [scrollbar-gutter:stable]"
            >
                {/* Wave hook only (`contents` keeps it out of layout): CSS ends the wave on .lobby-home-bd[data-home-wave-done]. */}
                <div ref={setHomeShellEl} className="lobby-home-bd contents">
                <PageShell width="wide">
                    {/* Order: hero | Your games → chips → search → grid */}
                    {/* Signed out: headline + animated game visual, as tall as "Your games" (wide layout). */}
                    {!isAuthenticated ? (
                        <GuestHero catalog={providers} />
                    ) : showContinueBlock ? (
                        <section
                            aria-label={
                                showContinueFallback
                                    ? t('home.popularGames', 'Popular games')
                                    : t('home.continuePlaying', 'Your games')
                            }
                            className="flex min-w-0 flex-col gap-3"
                            data-tour-id="tour.home.continue"
                        >
                            {/* Title row: the player's own games link to My games (the cards below are unchanged). */}
                            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
                                <h2 className="m-0 font-display text-title font-semibold text-primary">
                                    {showContinueFallback
                                        ? t('home.popularGames', 'Popular games')
                                        : t('home.continuePlaying', 'Your games')}
                                </h2>
                                {showContinueWallets ? (
                                    <Button
                                        variant="ghost"
                                        size="lg"
                                        icon={<Gamepad2 />}
                                        iconRight={<ChevronRight />}
                                        onClick={() => navigate('/money/games')}
                                    >
                                        {t('money.where.seeAllGames')}
                                    </Button>
                                ) : null}
                            </div>
                            {continueStripLoading ? (
                                <ContinueCarouselSkeleton label={t('common.loading', 'Loading…')} />
                            ) : (
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="hidden shrink-0 @bp-lg:block">
                                        <IconButton
                                            silent
                                            icon={<ChevronLeft />}
                                            label={t('home.continueScrollPrev', 'Scroll left')}
                                            disabled={!continueCarouselOverflow || !canScrollContinuePrev}
                                            onClick={() => scrollContinueCarousel(-1)}
                                        />
                                    </div>
                                    <ContinueCarouselViewport ref={continueCarouselRef}>
                                        <div className="flex w-max min-w-full items-stretch gap-3">
                                        {showContinueWallets
                                            ? continuePlayingWallets.map((wallet, cardIdx) => {
                                            const code = wallet.game?.code;
                                            const isComingSoon = wallet.game?.status === 'inactive';
                                            const catalogGame = providers.find(
                                                (x) => normalizeCode(x.code) === normalizeCode(code)
                                            );
                                            const playable = !isComingSoon && canPlay(wallet, catalogGame);
                                            return (
                                                <div
                                                    key={wallet.game?.id ?? wallet.account?.id ?? cardIdx}
                                                    data-continue-card=""
                                                    className={cx(
                                                        continueCardClass,
                                                        'cursor-pointer transition-colors duration-(--k-dur-fast) hover:border-edge-strong',
                                                        playHomeWave && ANIMATION_REGISTRY.wave.className,
                                                        isComingSoon && 'opacity-60 saturate-50'
                                                    )}
                                                    style={
                                                        playHomeWave
                                                            ? (getWaveTileStyle(0, cardIdx) as React.CSSProperties)
                                                            : undefined
                                                    }
                                                    onClick={() => {
                                                        if (isComingSoon) {
                                                            playClickSound();
                                                            notifyWalletComingSoon(wallet);
                                                            return;
                                                        }
                                                        const p = providers.find(
                                                            (x) =>
                                                                normalizeCode(x.code) === normalizeCode(code)
                                                        );
                                                        if (p) openDetail(p);
                                                    }}
                                                >
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <ContinueCardIcon
                                                            src={getIconPathForProvider(code || '')}
                                                            alt={wallet.game?.name || 'Game'}
                                                        />
                                                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                            <p className="m-0 truncate font-ui text-body font-semibold text-primary">
                                                                {wallet.game?.name || 'Game'}
                                                            </p>
                                                            <p className="m-0 flex min-w-0 items-baseline gap-1.5 font-ui text-small text-secondary">
                                                                <GameBalanceAmount item={wallet} size="small" />
                                                                <span className="truncate">{t('home.inGameLabel', 'in game')}</span>
                                                            </p>
                                                        </div>
                                                    </div>
                                                    {playable ? (
                                                        // Off kiosks: Play is the main button; Funds sits beside it in the
                                                        // same row and height, so the card keeps its size.
                                                        <div className="grid min-w-0 grid-cols-2 gap-2">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                fullWidth
                                                                icon={<Play />}
                                                                aria-label={t('play.playGame', {
                                                                    name: wallet.game?.name || 'Game',
                                                                })}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    play(wallet);
                                                                }}
                                                            >
                                                                {t('play.play')}
                                                            </Button>
                                                            {/* Not its own action: the tap reaches the card (game detail). */}
                                                            <Button variant="secondary" size="sm" fullWidth silent>
                                                                {t('home.manageFunds', 'Funds')}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        /* Not its own action: the tap reaches the card, which opens the game detail (as before). */
                                                        <Button variant="primary" size="sm" fullWidth silent>
                                                            {t('home.manageFunds', 'Funds')}
                                                        </Button>
                                                    )}
                                                </div>
                                            );
                                        })
                                            : continueFallbackProviders.map((provider, cardIdx) => (
                                                <div
                                                    key={provider.id}
                                                    data-continue-card=""
                                                    className={cx(
                                                        continueCardClass,
                                                        'cursor-pointer transition-colors duration-(--k-dur-fast) hover:border-edge-strong',
                                                        playHomeWave && ANIMATION_REGISTRY.wave.className
                                                    )}
                                                    style={
                                                        playHomeWave
                                                            ? (getWaveTileStyle(0, cardIdx) as React.CSSProperties)
                                                            : undefined
                                                    }
                                                    onClick={() => openDetail(provider)}
                                                >
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <ContinueCardIcon
                                                            src={
                                                                hasIconForProvider(provider.code)
                                                                    ? provider.icon
                                                                    : getIconPathForProvider(provider.code || '')
                                                            }
                                                            alt={provider.name}
                                                        />
                                                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                            <p className="m-0 truncate font-ui text-body font-semibold text-primary">
                                                                {provider.name}
                                                            </p>
                                                            <p className="m-0 truncate font-ui text-small text-secondary">
                                                                {provider.clientTag?.trim() ||
                                                                    t('home.suggestedLabel', 'Suggested')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <Button variant="primary" size="sm" fullWidth silent>
                                                        {t('home.manageFunds', 'Funds')}
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    </ContinueCarouselViewport>
                                    <div className="hidden shrink-0 @bp-lg:block">
                                        <IconButton
                                            silent
                                            icon={<ChevronRight />}
                                            label={t('home.continueScrollNext', 'Scroll right')}
                                            disabled={!continueCarouselOverflow || !canScrollContinueNext}
                                            onClick={() => scrollContinueCarousel(1)}
                                        />
                                    </div>
                                </div>
                            )}
                        </section>
                    ) : null}

                    <section
                        ref={setHomeContentEl}
                        aria-label={t('home.browseGames', 'Browse games')}
                        className="isolate flex min-w-0 flex-col gap-3"
                    >
                        <h2 className="m-0 font-display text-title font-semibold text-primary">
                            {t('home.browseGames', 'Browse games')}
                        </h2>

                        <div
                            ref={homeBrowseChipsRef}
                            className="flex min-w-0 flex-wrap items-center gap-2"
                            data-tour-id="tour.home.filters"
                        >
                            <FilterChips
                                aria-label={t('home.filters.label', 'Game categories')}
                                items={[
                                    { value: 'all', label: t('home.filters.all', 'All') },
                                    ...visibleTagFilters.map((tag) => ({ value: tag.id, label: tag.label })),
                                ]}
                                value={categoryFilter}
                                onChange={(value) => setFilter(value as string)}
                            />
                            {hiddenTagCount > 0 ? (
                                <FilterChip active={false} onClick={() => setTagsExpanded(true)}>
                                    {t('home.filters.moreTags', '+{{count}} more', { count: hiddenTagCount })}
                                </FilterChip>
                            ) : null}
                            {tagsExpanded && hasTagOverflow ? (
                                <FilterChip active={false} onClick={() => setTagsExpanded(false)}>
                                    {t('home.filters.lessTags', 'Less')}
                                </FilterChip>
                            ) : null}
                        </div>

                        <div data-tour-id="tour.home.search">
                            <SearchField
                                aria-label={t('games.searchGamesPlaceholder', 'Search games…')}
                                placeholder={t('games.searchGamesPlaceholder', 'Search games…')}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                clearLabel={t('common.clearSearch')}
                            />
                        </div>

                    {isLoadingProviders && (
                        <div className="lobby-home-grid" style={homeGridStyle} data-tour-id="tour.home.grid">
                            {Array.from({ length: homeSkeletonTileCount }, (_, i) => (
                                <HomeGridTileSkeleton key={`home-skel-${i}`} />
                            ))}
                        </div>
                    )}

                    {!isLoadingProviders && providersError && (
                        <Alert tone="danger">{providersError}</Alert>
                    )}

                    {!isLoadingProviders && providers.length > 0 && (
                        <div
                            style={
                                homeGridMinHeight > 0 && !searchQuery.trim()
                                    ? { minHeight: homeGridMinHeight }
                                    : undefined
                            }
                        >
                            {filteredCards.length > 0 ? (
                                <div
                                    key={`home-grid-${categoryFilter}-${searchQuery.trim()}`}
                                    ref={homeBrowseGridRef}
                                    className="lobby-home-grid"
                                    style={homeBrowseGridStyle}
                                    data-tour-id="tour.home.grid"
                                >
                                    {filteredCards.map((provider, index) => {
                                        const isComingSoon = provider.status === 'inactive';
                                        return (
                                        <GameTile
                                            key={provider.id}
                                            name={provider.name}
                                            imageUrl={hasIconForProvider(provider.code) ? provider.icon : undefined}
                                            hot={!!provider.clientTag}
                                            comingSoon={isComingSoon}
                                            comingSoonLabel={comingSoonLabel}
                                            statusBadge={
                                                isProviderDepositLocked(provider)
                                                    ? t('home.maintenance', 'Maintenance')
                                                    : undefined
                                            }
                                            onClick={() => openDetail(provider)}
                                            waveRow={
                                                playHomeWave
                                                    ? waveRowOffset + Math.floor(index / lobbyCols)
                                                    : undefined
                                            }
                                            waveCol={index % lobbyCols}
                                            shineRow={
                                                waveGridEnabled
                                                    ? Math.floor(index / lobbyCols)
                                                    : undefined
                                            }
                                            shineCol={index % lobbyCols}
                                        />
                                        );
                                    })}
                                </div>
                            ) : (
                                <Card padding="none">
                                    <EmptyState
                                        icon={<SearchX />}
                                        title={
                                            searchQuery.trim()
                                                ? t('home.noSearchResults', 'No games found matching "{{query}}"', { query: searchQuery })
                                                : t('home.noFilterResults', 'No games in this category')
                                        }
                                        action={
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                silent
                                                onClick={() => { playClickSound(); setSearchQuery(''); setCategoryFilter('all'); }}
                                            >
                                                {t('home.clearFilters', 'Clear filters')}
                                            </Button>
                                        }
                                    />
                                </Card>
                            )}
                        </div>
                    )}

                    {!isLoadingProviders && providers.length === 0 && !providersError && (
                        <Alert tone="warning">
                            {t('home.noProviders', 'No game providers available at the moment')}
                        </Alert>
                    )}
                    </section>

                    {/* Pay your way: the payment logos, for guests and players, on every surface (no links). */}
                    <PaymentMethodsStrip />
                </PageShell>
                </div>
            </div>

        </main>

        {/* Order summary only when cart has amounts (not on pure guest browse) */}
        {totalAmount > 0 && (
            <OrderSummaryFooter
                className="flex-shrink-0"
                totalAmount={totalAmount}
                onClearOrder={handleResetAll}
                onProceedToFunding={handleProceedToFunding}
                onShowOrderSummary={() => setShowOrderSummary(true)}
            />
        )}

        {/* Order Summary Modal */}
        <OrderSummaryModal
            isOpen={showOrderSummary}
            onClose={() => setShowOrderSummary(false)}
            totalAmount={totalAmount}
            providerAmounts={providerAmounts}
            providers={providers}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onRemoveItem={handleRemoveItem}
            selectedProvider={null}
            selectedAction="deposit"
            onConfirm={() => {
                setShowOrderSummary(false);
                handleProceedToFunding();
            }}
        />

        {/* Manual Deposit Warning Modal */}
        <ManualDepositWarningModal
            isOpen={showManualDepositWarning}
            onClose={() => setShowManualDepositWarning(false)}
            onConfirm={executeProceedToFunding}
            amount={getManualProvidersTotalAmount()}
            providerName={getSelectedManualProviders().map(p => p.name).join(', ')}
        />

        {/* Play off kiosks: "Your login" (games not allowed inside the app) */}
        {playDialog}

        {/* Game sheet: art, notes, login, Add money and Take money out */}
        <GameSheet
            game={detailProvider}
            wallet={detailWallet}
            onClose={() => setDetailProvider(null)}
            onWithdrawn={() => {
                if (isAuthenticated) void refreshGameWallets();
            }}
        />
    </div>);
}

// DEMO MODE - REMOVE IN PRODUCTION
// To disable demo mode: Comment out the next 2 lines and uncomment the last line
// To enable demo mode: Uncomment the next 2 lines and comment out the last line
export default function KioskGameProviders() {
    return (
        <DemoWrapper>
            <KioskGameProvidersContent />
        </DemoWrapper>
    );
}

// PRODUCTION MODE - Use this when demo mode is disabled
// export default KioskGameProvidersContent;
